import React, { useState, useEffect } from 'react';
import { FaShoppingBasket, FaEye, FaSearch, FaFileExport, FaDownload } from "react-icons/fa";
import { GrPowerReset } from "react-icons/gr";
import { MdPayments } from "react-icons/md";
import { IoIosSend } from "react-icons/io";
import { Link } from 'react-router-dom';
import { Spinner, Table, Button, Form } from 'react-bootstrap';
import MUIDataTable from "mui-datatables";
import ThemeModal from '../../../components/cmp/ThemeModal';
import { Alerts, GetOrders } from '../../../helpers';
import { useHasPermission } from '../../../hooks/useRoleCheck';
import { Checkbox } from '@mui/material';
import Card from 'react-bootstrap/Card';
import { orderStatusMap, paymentStatusMap } from '../../../utils/statusFormat';
const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentModal, setPaymentModal] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);


  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);

  // New state for filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [orderStatus, setOrderStatus] = useState('');

  const isManagerOrSuperAdmin = useHasPermission(["ROLE_SUPER_ADMIN", "ROLE_MANAGER"]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const resp = await GetOrders.getAllOrders({
        page: page + 1,
        limit: rowsPerPage,
        search: searchText || '',
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        paymentStatus: paymentStatus || undefined,
        orderStatus: orderStatus || undefined
      });
      if (resp && resp.status) {
        setOrders(resp.orders);
        setTotalCount(resp.totalOrders || 0);
      } else {
        setOrders([]);
        setTotalCount(0);
      }
    } catch (err) {
      Alerts.alertMessage('Failed to fetch orders', 'top-right', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, rowsPerPage, searchText, startDate, endDate, paymentStatus, orderStatus]);

  const handleSearch = () => {
    setPage(0);
    setSearchText(searchInput.trim());
  };

  const getPaymentDetails = (id) => {
    const f = orders.find(o => o.id === id);
    if (f) {
      setPaymentDetails(f.payment);
      setPaymentModal(true);
    }
  };

  const handleResendPayment = async (order_number) => {
    setResendLoading(true);
    setCurrentOrder(order_number);
    const f = orders.find(o => o.order_number === order_number);
    if (f) {
      const resp = await GetOrders.resendCheckoutSession({ order: f });
      if (resp && resp.status) {
        Alerts.alertMessage(resp.message, 'top-right', 'success');
      } else {
        Alerts.alertMessage('Something went wrong!', 'top-right', 'error');
      }
    }
    setResendLoading(false);
    setCurrentOrder(null);
  };

  const handleRowSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const successfulOrders = orders
      // .filter(item => item.payment_status === 'succeeded')
      .map(item => item.order_number);

    if (selectedIds.length === successfulOrders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(successfulOrders);
    }
  };

  const CustomHeaderCheckbox = () => (
    <Checkbox
      onChange={handleSelectAll}
      checked={selectedIds.length === orders.length && orders.length > 0}
      indeterminate={selectedIds.length > 0 && selectedIds.length < orders.length}
    />
  );

  const CustomRowCheckbox = ({ rowIndex }) => {
    const id = orders[rowIndex]?.order_number;
    const paymentStatus = orders[rowIndex]?.payment_status;
    return (
      <Checkbox
        checked={selectedIds.includes(id)}
        onChange={() => handleRowSelect(id)}
      // disabled={paymentStatus !== 'succeeded'}
      />
    );
  };
 





  const columns = [
    {
      name: 'SL',
      options: {
        customHeadRender: () => <div style={{ marginLeft: 10 }}><CustomHeaderCheckbox /></div>,
        customBodyRender: (value, tableMeta) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CustomRowCheckbox rowIndex={tableMeta.rowIndex} />
            <span style={{ marginLeft: 8 }}>{value}</span>
          </div>
        ),
      }
    },
    { name: 'Order ID' },
    { name: 'Date' },
    { name: 'Customer' },
    { name: 'Items' },
    { name: 'Total' },
    { name: 'Payment Status', options: { filter: false } },
    { name: 'Order Status', options: { filter: false } },
    { name: 'Actions', options: { filter: false, sort: false } },
  ];

  const groupItems = (ordered_items) => {
  const bulkGroupsMap = {};
  const regularItems = [];

  ordered_items.forEach(item => {
    if (item.is_bulk_order && item.truck_group_id) {
      if (!bulkGroupsMap[item.truck_group_id]) {
        bulkGroupsMap[item.truck_group_id] = {
          truck_group_id: item.truck_group_id,
          order_type: item.order_type,
          items: []
        };
      }
      bulkGroupsMap[item.truck_group_id].items.push(item);
    } else {
      regularItems.push(item);
    }
  });

  return {
    regular: regularItems,
    bulkGroups: Object.values(bulkGroupsMap)
  };
};


  const data = orders.map((order, index) => {
     const grouped = groupItems(order.ordered_items || []);
     console.log(index+1, grouped);
    return [
    page * rowsPerPage + index + 1,
    <Link key={`link-${order.id}`} to={`/app/order/${order.order_number}`}><b># {order.order_number}</b></Link>,
    formatDate(order.createdAt),
    order.user?.name || '-',
    grouped.regular.length + grouped.bulkGroups.length || 0,
    <span style={{ minWidth: 70 }}>₹ {order.total_amount}</span>,
    <span className={`badge ${paymentStatusMap[order.payment?.payment_status]?.class || ""}`}>
      {paymentStatusMap[order.payment?.payment_status]?.label || ""}
    </span>,
        <span className={`badge ${orderStatusMap[order.order_status]?.class || ""}`}>
      {orderStatusMap[order.order_status]?.label || ""} 
    </span>,
  
    <div className={`actions`} style={{ width: "150px" }}>
      <Link to={`/app/order/${order.order_number}`} className="btn btn-info btn-sm"><FaEye size={14} /></Link> &nbsp;
      {/* {
        order.payment_status !== 'pending' &&
        <button onClick={() => getPaymentDetails(order.id)} className="btn btn-success btn-sm" data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Payments"><MdPayments size={18} /></button>
      } */}
      {
        order.payment_status !== 'pending' &&
        <Link to={`/app/order/payments/${order.order_number}`} className="btn btn-success btn-sm" data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Payments"><MdPayments size={18} /></Link>
      }
      &nbsp;
      {
        isManagerOrSuperAdmin &&
        order.payment_status === 'pending' &&
        <button className="btn btn-secondary btn-sm" onClick={() => handleResendPayment(order.order_number)} data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Payment Link" disabled={resendLoading}>
          {resendLoading && currentOrder === order.order_number ? <Spinner size="sm" /> : <IoIosSend size={18} />}
        </button>
      }
    </div>
  ]
}
);

  const options = {
    serverSide: true,
    count: totalCount,
    page: page,
    rowsPerPage: rowsPerPage,
    filter: false,
    search: false,
    download: false,
    print: false,
    selectableRows: 'none',
    rowsSelected: selectedIds
      .map(id => orders.findIndex(item => item.order_number === id))
      .filter(index => index !== -1),
    onTableChange: (action, tableState) => {
      if (action === 'changePage') {
        setPage(tableState.page);
      } else if (action === 'changeRowsPerPage') {
        setRowsPerPage(tableState.rowsPerPage);
        setPage(0);
      }
    },
  };

  const handleMultipleInvoiceDownload = async () => {
    try {
      setInvoiceLoading(true);
      const resp = await GetOrders.generateOrdersInvoices({ orderIds: selectedIds });
      if (resp && resp.status) {
        const response = await fetch(resp.downloadZipUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch ZIP file');
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoices_${Date.now()}.zip`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }
      setSelectedIds([]);
      setInvoiceLoading(false);
    } catch (error) {
      console.error('Download failed:', error);
      setInvoiceLoading(false);
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setPaymentStatus('');
    setOrderStatus('');
    setSearchInput('');
    setSearchText('');
    setPage(0);
  };


  const handleExportOrder = async () => {
    try {
      setDownloadLoading(true);
      const resp = await GetOrders.exportUsers(selectedIds);
      if (resp) {
        const url = window.URL.createObjectURL(new Blob([resp]));
        const link = document.createElement('a');
        link.href = url;
        const now = new Date();
        const fileName = `${now.getDate()} ${now.toLocaleString('en-GB', { month: 'long' })} ${now.getFullYear()}`;
        link.setAttribute('download', `order_report-${fileName}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setDownloadLoading(false);
      }

    } catch (err) {
      console.error('Download failed:', err);
    }
  };


  return (
    <div className="container-fluid">
      <div className="page-title mb-3 d-flex justify-content-between align-items-center">
        <h2><FaShoppingBasket /> Orders</h2>
        <div className="searchInput">
          <div class="input-group">
            <input type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              class="form-control"
              placeholder="Search by Order ID or Customer" />
            <button class="btn btn-outline-secondary" type="button" onClick={handleSearch}><FaSearch /></button>
          </div>
        </div>
      </div>

      {/* Filter UI */}
      <div className="order-filter mb-3 d-flex gap-3 align-items-center flex-wrap">
        <div>
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div>
          <Form.Label>End Date</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div>
          <Form.Label>Payment Status</Form.Label>
          <Form.Select
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="partially_refunded">Partially Refunded</option>
            <option value="refunded">Refunded</option>
          </Form.Select>
        </div>
        <div>
          <Form.Label>Order Status</Form.Label>
          <Form.Select
            value={orderStatus}
            onChange={(e) => setOrderStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="out_for_delivery">Out For Delivery</option>
            <option value="delivered">Delivered</option>
             <option value="cancelled">Cancelled</option>
            <option value="returned">Returned</option>
          </Form.Select>
        </div>
        <Button variant="secondary" onClick={handleResetFilters}>
          <GrPowerReset /> Reset Filters
        </Button>
        {
          selectedIds.length <= 0 && <button className="btn btn-primary btn-sm" onClick={handleExportOrder} disabled={downloadLoading}>
            {downloadLoading ? <><Spinner size="sm" /> Please wait..</> : <><FaFileExport /> Export Orders</>}
          </button>
        }

      </div>

      {!isLoading ? (
        <>
          {selectedIds.length > 0 && (
            <div className="selected-actions">
              <p style={{ display: 'inline-block', marginRight: '15px' }}>
                <strong>{selectedIds.length}</strong> item(s) selected
              </p>
              <div className='d-flex align-items-center'>
                <button
                  className="btn btn-default btn-sm"
                  style={{ marginRight: '10px', padding: '7px 22px' }}
                  onClick={handleMultipleInvoiceDownload}
                  disabled={invoiceLoading}
                >
                  {invoiceLoading ? <><Spinner size="sm" /> Please wait..</> : <><FaDownload /> Download Invoice{selectedIds.length <= 1 ? '' : 's'} </>}
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleExportOrder}>
                  {downloadLoading ? <><Spinner size="sm" /> Please wait..</> : <><FaFileExport /> Export Selected Orders</>}
                </button>
              </div>

            </div>
          )}
          <MUIDataTable
            className="orderTable"
            data={data}
            columns={columns}
            options={options}
          />
        </>
      ) : (
        <div className="text-center"><Spinner animation="border" /></div>
      )}

      <ThemeModal show={paymentModal} handleHide={() => setPaymentModal(false)} title="Payment Details" isFullscreen={true} centered>
        <div className='row'>
          <div className='col-md-5'>
            <Card>
              <Card.Header><h5>Initial Payment</h5></Card.Header>
              <Card.Body>
                {paymentDetails ? (
                  <>

                    <Table className='ft-14'>
                      <tbody>
                        <tr><th>Transaction ID</th><td>{paymentDetails.transaction_id || paymentDetails.intent_id}</td></tr>
                        <tr><th>Order Number</th><td>{paymentDetails.order_number}</td></tr>
                        <tr><th>Amount</th><td>£ {paymentDetails.amount}</td></tr>
                        <tr><th>Currency</th><td>{paymentDetails.currency}</td></tr>
                        <tr><th>Payment Method</th><td>{paymentDetails.payment_method || paymentDetails.method}</td></tr>
                        <tr><th>Payment Status</th><td>{paymentDetails.payment_status || paymentDetails.status}</td></tr>
                        <tr><th>Payment Date</th><td>{paymentDetails.payment_date}</td></tr>
                      </tbody>
                    </Table>
                    <div className='completeDetails'>
                      <h6 style={{ marginBottom: 10, marginTop: 20 }}>Complete Payment Details</h6>
                      <code>
                        {paymentDetails.complete_details}
                      </code>
                    </div>
                  </>
                ) : (
                  <p>No details available.</p>
                )}

              </Card.Body>
            </Card>

          </div>
          <div className='col-md-7'>
            <Card>
              <Card.Header><h5>Refunds</h5></Card.Header>
              <Card.Body>

              </Card.Body>
            </Card>
          </div>
        </div>

      </ThemeModal>
    </div>
  );
};

export default Orders;